/**
 * Smart Product Tabs
 * Handles tab switching with accessible keyboard navigation
 */
(function () {
  'use strict';

  class SmartProductTabs {
    constructor(container) {
      this.container = container;
      if (!this.container) return;

      this.tabs = Array.from(
        this.container.querySelectorAll('.smart-product-tabs__tab')
      );
      this.panels = Array.from(
        this.container.querySelectorAll('.smart-product-tabs__panel')
      );

      if (this.tabs.length === 0) return;

      this.bindEvents();
    }

    bindEvents() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => this.onTabClick(e));
        tab.addEventListener('keydown', (e) => this.onTabKeydown(e));
      });

      // Handle video play buttons
      this.container.addEventListener('click', (e) => {
        const playBtn = e.target.closest('.smart-tab-video__play-btn');
        if (playBtn) {
          this.playVideo(playBtn);
        }
      });
    }

    onTabClick(e) {
      const tab = e.currentTarget;
      const index = parseInt(tab.dataset.tabIndex, 10);
      this.activateTab(index);
    }

    onTabKeydown(e) {
      const currentIndex = this.tabs.indexOf(e.currentTarget);
      let newIndex;

      switch (e.key) {
        case 'ArrowLeft':
          newIndex =
            currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
          break;
        case 'ArrowRight':
          newIndex =
            currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      this.tabs[newIndex].focus();
      this.activateTab(newIndex);
    }

    activateTab(index) {
      // Deactivate all
      this.tabs.forEach((tab) => {
        tab.classList.remove('smart-product-tabs__tab--active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      this.panels.forEach((panel) => {
        panel.classList.remove('smart-product-tabs__panel--active');
        panel.setAttribute('hidden', '');
      });

      // Activate selected
      const tab = this.tabs[index];
      const panel = this.panels[index];

      if (tab) {
        tab.classList.add('smart-product-tabs__tab--active');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');
      }

      if (panel) {
        panel.classList.add('smart-product-tabs__panel--active');
        panel.removeAttribute('hidden');
      }
    }

    playVideo(btn) {
      const videoUrl = btn.dataset.videoUrl;
      const videoId = btn.dataset.videoId;
      if (!videoUrl && !videoId) return;

      const thumbnail = btn.closest('.smart-tab-video__thumbnail');
      if (!thumbnail) return;

      // Use data-video-id if available, otherwise extract from URL
      let youtubeId = videoId || '';
      if (!youtubeId) {
        // Try youtu.be format
        const shortMatch = videoUrl.match(/youtu\.be\/([^?&]+)/);
        if (shortMatch) {
          youtubeId = shortMatch[1];
        } else {
          // Try youtube.com/watch?v= format
          const longMatch = videoUrl.match(/[?&]v=([^&]+)/);
          if (longMatch) {
            youtubeId = longMatch[1];
          }
        }
      }

      if (youtubeId) {
        thumbnail.innerHTML = `
          <iframe
            src="https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            style="position:absolute;top:0;left:0;width:100%;height:100%;"
          ></iframe>
        `;
        thumbnail.style.position = 'relative';
        thumbnail.style.paddingBottom = '56.25%';
        thumbnail.style.height = '0';
      } else {
        // Fallback: open in new tab
        window.open(videoUrl, '_blank');
      }
    }
  }

  // Initialize
  const initSmartProductTabs = () => {
    document
      .querySelectorAll('[data-smart-product-tabs]')
      .forEach((container) => new SmartProductTabs(container));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmartProductTabs);
  } else {
    initSmartProductTabs();
  }
})();
